<template>
  <div class="ModalNewEntities">
    <a-button type="primary" class="button--create" @click="handleShowModal">
      Crear nueva base
    </a-button>
    <a-modal
      :visible="visible"
      width="450px"
      centered
      :body-style="{ padding: 0 }"
      :mask-closable="false"
      :keyboard="false"
      @cancel="handleConfirmationClose"
    >
      <template v-slot:title>
        <span> Crear nueva base </span>
      </template>
      <a-form class="form" layout="vertical" ref="formRef" :model="formState" :rules="rules">
        <a-form-item name="name" label="Nombre de la base">
          <a-auto-complete
            v-model:value="formState.name"
            placeholder="Escribir nombre de la base"
            @input="showConfirmModal = true"
            @search="handleSearchName"
            :class="{ 'ant-form-item-has-error': isNameDuplicated }"
          />
          <span v-if="isNameDuplicated" class="form__error">Este nombre ya esta en uso</span>
        </a-form-item>
        <a-form-item
          name="entity"
          label="Selecciona una empresa"
          v-if="typeBusiness === TypeBusiness.contactCenter"
        >
          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="formState.entity"
            placeholder="Seleccionar una empresa"
            @change="handleEntityChange"
          >
            <a-select-option v-for="entity in entities" :key="entity.id">
              {{ entity.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item name="portfolio" label="Selecciona una campaña">
          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="formState.portfolio"
            placeholder="Selecionar una campaña"
            @change="handlePortfolioChange"
          >
            <a-select-option v-for="portfolio in portfolios" :key="portfolio.id">
              {{ portfolio.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <p class="space">Selecciona rango de fechas</p>

        <a-row :gutter="24">
          <a-col span="12">
            <a-form-item name="date_start" label="Desde">
              <a-date-picker
                v-model:value="formState.date_start"
                placeholder="-- / -- / --"
                :show-today="false"
                :format="humanDateFormat"
                @change="showConfirmModal = true"
                :disabled-date="disabledDate"
              />
            </a-form-item>
          </a-col>
          <a-col span="12">
            <a-form-item name="date_finish" label="Hasta">
              <a-date-picker
                v-model:value="formState.date_finish"
                placeholder="-- / -- / --"
                :show-today="false"
                :format="humanDateFormat"
                @change="showConfirmModal = true"
                :disabled-date="disabledEndDate"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <div class="container__expire">
          <p class="space">Acciones al caducar tu base</p>
          <a-form-item class="switch--small">
            <a-checkbox v-model:checked="formState.enable_regeneration" size="small" />
            Habilitar la regeneración de esta base
            <a-tooltip>
              <template v-slot:title>
                Se reactivará esta base desde su fecha de finalización y se utilizará el mismo
                intervalo de tiempo establecido en esta base.
              </template>
              <InfoCircleOutlined />
            </a-tooltip>
          </a-form-item>

          <a-form-item name="assignment_type">
            <a-radio-group
              v-model:value="enable_still_tipificactions"
              @change="showConfirmModal = true"
              :disabled="!formState.enable_regeneration"
            >
              <a-tooltip v-if="!formState.enable_regeneration">
                <template v-slot:title>
                  Para habilitar esta opción, debe activar la opción superior
                </template>
                <a-radio :value="false" :style="radioStyle" class="separator__radio">
                  Regenerar clientes en blanco (Sin resultados generados por tipificaciones)
                </a-radio>
              </a-tooltip>
              <a-radio
                :value="false"
                :style="radioStyle"
                class="separator__radio"
                v-if="formState.enable_regeneration"
              >
                Regenerar clientes en blanco (Sin resultados generados por tipificaciones)
              </a-radio>
              <div class="separator__group"></div>
              <a-radio :value="true" :style="radioStyle" class="separator__radio">
                Regenerar clientes manteniendo los resultados generados por tipificaciones
              </a-radio>
            </a-radio-group>
          </a-form-item>
        </div>
      </a-form>
      <!-- Fotter Modal -->
      <template v-slot:footer>
        <a-button key="back" @click="handleConfirmationClose"> Cancelar </a-button>
        <a-button key="submit" type="primary" @click="handleOk" :disabled="isDisabled">
          Crear base
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { humanDateFormat } from "@/app/shared/utils/constants";
import { defineComponent, ref, reactive, toRaw, createVNode } from "vue";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { UserStatus } from "@/app/shared/utils/enums";
import { TypeBusiness } from "@/app/shared/utils/enums";
import { Modal } from "ant-design-vue";
import { disabledDate } from "@/app/shared/utils/dates";

const MIN_LETTERS_TO_SEARCH = 0;

export default defineComponent({
  components: {
    InfoCircleOutlined,
  },
  props: {
    typeBusiness: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const formRef = ref();
    const enable_still_tipificactions = ref(false);
    const showConfirmModal = ref(false);
    const visible = ref(false);
    const formState = reactive({
      name: "",
      entity: undefined,
      portfolio: undefined,
      date_start: "",
      enable_regeneration: false,
    });
    const rules = {
      name: [{ required: true, message: "Campo requerido" }],
      entity: [{ required: true, message: "Campo requerido" }],
      portfolio: [{ required: true, message: "Campo requerido" }],
      date_start: [{ type: "object", required: true, message: "Campo requerido" }],
      date_finish: [{ type: "object", required: true, message: "Campo requerido" }],
      enable_regeneration: [{}],
      enable_still_tipificactions: [{}],
    };
    const handleConfirmationClose = () => {
      if (showConfirmModal.value) {
        Modal.confirm({
          title: "¿Deseas descartar los cambios realizados?",
          content: "Al cerrar no se guardarán los datos previamente ingresados",
          icon: createVNode(QuestionCircleOutlined),
          onOk: () => handleCancel(),
          centered: true,
          okText: "Sí, descartar cambios",
          cancelText: "No, seguir editando",
        });
      } else {
        handleCancel();
      }
    };
    const handleCancel = () => {
      visible.value = false;
      showConfirmModal.value = false;
      formRef.value.resetFields();
    };
    return {
      formRef,
      formState,
      rules,
      enable_still_tipificactions,
      handleConfirmationClose,
      showConfirmModal,
      handleCancel,
      visible,
    };
  },
  data() {
    return {
      humanDateFormat,
      config: {
        rules: [{ type: "object", required: true, message: "Campo requerido" }],
      },
      UserStatus,
      TypeBusiness,
    };
  },
  computed: {
    selectedEntity: {
      get() {
        return this.$store.getters["getSelectedEntity"];
      },
      set(entityId) {
        this.$store.dispatch("setSelectedEntity", entityId);
      },
    },
    selectedPortfolio: {
      get() {
        return this.$store.getters["getSelectedPortfolio"];
      },
      set(portfolioId) {
        this.$store.dispatch("setSelectedPortfolio", portfolioId);
      },
    },
    entities() {
      return this.$store.getters["getEntityList"];
    },
    portfolios() {
      return this.$store.getters["getFilteredPortfolios"];
    },
    isDisabled() {
      return (
        this.formState.name == "" ||
        this.formState.entity == undefined ||
        this.formState.portfolio == undefined ||
        this.formState.date_start == ""
      );
    },
    getAllBases() {
      return this.$store.getters["getAllBases"];
    },
    isNameDuplicated() {
      return (
        this.getAllBases?.length > 0 &&
        this.getAllBases[0].name.toLowerCase() === this.formState.name.toLowerCase()
      );
    },
  },
  created() {
    this.$store.dispatch("fetchAllEntities");
    this.$store.dispatch("fetchAllPortfoliosByEntity");
  },
  methods: {
    disabledDate(current) {
      this.formState.date_finish = "";
      return disabledDate(current);
    },
    disabledEndDate(endValue) {
      if (!endValue || !this.formState.date_start) {
        return disabledDate(endValue);
      }
      return this.formState.date_start.valueOf() >= endValue.valueOf();
    },
    handleShowModal() {
      this.visible = true;
      setTimeout(() => this.setSelectedFields(), 10);
    },
    setSelectedFields() {
      this.formRef.entity = this.selectedEntity == 0 ? undefined : this.selectedEntity;
      this.formRef.portfolio = this.selectedPortfolio == 0 ? undefined : this.selectedPortfolio;
    },
    handleEntityChange(entity) {
      this.formState.portfolio = undefined;
      this.$store.dispatch("fetchAllPortfoliosByEntity", {
        filters: {
          entity: entity,
        },
      });
      this.formRef.portfolio = undefined;
      this.showConfirmModal = true;
    },
    handlePortfolioChange(portfolioId) {
      const portfolio = this.portfolios.find((item) => portfolioId == item.id);

      this.formRef.entity = portfolio.entity.id;
      this.showConfirmModal = true;
    },
    handleOk() {
      this.formRef.validateFields().then(async () => {
        try {
          this.visible = false;
          let values = toRaw(this.formState);
          const data = {
            ...values,
            number_clients: 0,
            enable_still_tipificactions: this.enable_still_tipificactions,
          };
          await this.$store.dispatch("createNewBase", data);
          this.$message.success(`Se ha creado la nueva base ${this.formState.name}`);
          this.formState.enable_regeneration = false;
          this.formRef.resetFields();
        } catch (error) {
          this.$message.error(`Ha ocurrido un error`);
          this.formState.enable_regeneration = false;
          this.formRef.resetFields();
        }
      });
    },
    handleSearchName(data) {
      if (data.length > MIN_LETTERS_TO_SEARCH) {
        this.handleFetchBase(data);
      } else {
        this.$store.dispatch("clearInputBase");
      }
    },
    async handleFetchBase(data) {
      await this.$store.dispatch("fetchListBases", data);
    },
  },
  watch: {
    visible(value) {
      if (value && this.typeBusiness == 2) {
        this.selectedEntity = this.entities.find((item) => item.is_owner == true).id;
        this.formState.entity = this.entities.find((item) => item.is_owner == true).id;
      }
    },
  },
});
</script>

<style lang="sass" scoped>
.button--create
  margin-top: 4px

.form
  display: block
  padding: 20px 0
  max-width: 340px
  margin: 0 auto

.space
  margin-bottom: 8px
  margin-top: 20px

.small
  max-height: 20px


.ant-form-item
  margin-bottom: 12px

.flag
  width: 20px
  margin-bottom: 2px
.textdisabled
  color: $gray-6

.container__expire
  margin-bottom: 15px

.switch--small
  margin-bottom: 4px

.separator__radio
  white-space: pre-line
  line-height: 20px !important
  display: flex !important
  margin-top: 10px
  align-items: center !important
  margin-bottom: 10px

.separator__group
  margin-bottom: 20px

.form__error
  color: $red-6
  margin: 0

:deep(.ant-form-item-required)
  margin-bottom: 2px
  font-weight: 600 !important
  color: $gray-9
</style>
